// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PinInput } from '@mantine/core';
import { Anchor, Badge, Button, Center, Container, Stack, Text, Title } from '@/components/ui';
import { IconArrowLeft, IconDeviceMobile, IconColumns, IconCheck, IconFingerprint, IconShieldLock, IconDevices } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { SplitScreenLayout } from '../components';

const features = [
  {
    icon: <IconFingerprint size={20} />,
    title: 'Biometric Support',
    description: 'Use fingerprint or face recognition',
  },
  {
    icon: <IconShieldLock size={20} />,
    title: 'Extra Security',
    description: 'Additional layer of protection',
  },
  {
    icon: <IconDevices size={20} />,
    title: 'Trusted Devices',
    description: 'Remember your devices for faster login',
  },
];

export default function OtpSplitScreen() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  return (
    <SplitScreenLayout
      ratio="40-60"
      leftContentType="features"
      gradientFrom="violet"
      gradientTo="purple"
      title="Two-Factor Authentication"
      subtitle="Protect your account with an extra layer of security using OTP verification."
      features={features}
    >
      <Container size="xs" w="100%">
        <Stack gap="lg">
          <Button
            component={Link}
            to="/demo/auth-pages/otp"
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            w="fit-content"
          >
            Back to variants
          </Button>

          {isVerified ? (
            <FadeIn>
              <Stack gap="lg" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: 'var(--mantine-color-green-light)',
                  }}
                >
                  <IconCheck size={40} color="var(--mantine-color-green-6)" />
                </Center>

                <Stack align="center" gap="xs">
                  <Title order={2} ta="center">Verified!</Title>
                  <Text c="dimmed" ta="center" maw={300}>
                    Your identity has been successfully verified. You can now access your account.
                  </Text>
                </Stack>

                <Button
                  component={Link}
                  to="/demo/auth-pages/login/splitscreen"
                  fullWidth
                  size="md"
                  variant="gradient"
                  gradient={{ from: 'violet', to: 'purple' }}
                >
                  Continue to dashboard
                </Button>
              </Stack>
            </FadeIn>
          ) : (
            <>
              <FadeIn delay={100}>
                <Stack gap="xs" align="center">
                  <Badge
                    leftSection={<IconColumns size={12} />}
                    variant="gradient"
                    gradient={{ from: 'violet', to: 'purple' }}
                    size="lg"
                  >
                    Split Screen
                  </Badge>
                  <Center
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: 'var(--mantine-color-violet-light)',
                      marginTop: 'var(--mantine-spacing-sm)',
                    }}
                  >
                    <IconDeviceMobile size={28} color="var(--mantine-color-violet-6)" />
                  </Center>
                  <Title order={2} ta="center" mt="sm">
                    Verification code
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={320}>
                    We've sent a 6-digit verification code to your phone ending in **42
                  </Text>
                </Stack>
              </FadeIn>

              <SlideIn direction="up" delay={200}>
                <form onSubmit={handleSubmit}>
                  <Stack gap="lg">
                    <Center>
                      <PinInput
                        length={6}
                        size="lg"
                        value={otp}
                        onChange={handleChange}
                        type="number"
                        oneTimeCode
                        aria-label="Verification code"
                      />
                    </Center>

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      variant="gradient"
                      gradient={{ from: 'violet', to: 'purple' }}
                      disabled={!isComplete}
                    >
                      Verify code
                    </Button>
                  </Stack>
                </form>
              </SlideIn>

              <FadeIn delay={300}>
                <Text ta="center" size="sm" c="dimmed">
                  Didn't receive the code?{' '}
                  <Anchor component="button" type="button" fw={500}>
                    Resend
                  </Anchor>
                </Text>
              </FadeIn>

              <FadeIn delay={400}>
                <Center>
                  <Anchor component={Link} to="/demo/auth-pages/login/splitscreen" size="sm">
                    <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Back to login
                  </Anchor>
                </Center>
              </FadeIn>
            </>
          )}
        </Stack>
      </Container>
    </SplitScreenLayout>
  );
}
